import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import NavBar from '../components/NavBar';
import BackToCalculatorFab from '../components/BackToCalculatorFab';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Best Marathon Racing Shoes 2025: Top 10 Carbon Plate Picks for Every Runner',
  description:
    'Reviews of the ten best carbon plate marathon racing shoes of 2025, including the Nike Alphafly 3, Vaporfly 4, Adidas Adios Pro 4, Asics Metaspeed Sky Paris, New Balance FuelCell SC Elite v5, Saucony Endorphin Elite 2, Hoka Rocket X 3, Brooks Hyperion Elite 5, Puma Fast-R Nitro Elite 3, and On Cloudboom Strike.',
  author: { '@type': 'Organization', name: 'SpeedBoostShoes.com' },
  publisher: { '@type': 'Organization', name: 'SpeedBoostShoes.com', url: 'https://www.speedboostshoes.com/' },
  datePublished: '2025-01-01',
  dateModified: '2026-03-05',
  mainEntityOfPage: 'https://www.speedboostshoes.com/best-marathon-racing-shoes-2025',
};

const shoes = [
  {
    rank: 1,
    name: 'Nike Alphafly 3',
    price: '~$285',
    weight: '~8.4 oz',
    best: 'Maximum cushioning for the marathon',
    image: '/imgs/nike_alphafly_3.avif',
    verdict:
      'The most cushioned super shoe on the market. Two forefoot Air Zoom pods plus 40mm of ZoomX foam deliver unmatched energy return for the half marathon and full marathon. Heavier than the Vaporfly but the extra cushioning pays off in the final miles of a long race when legs are fatigued.',
    pros: ['Maximum cushioning and energy return', 'Excellent for the marathon distance', 'Most forgiving on fatigued legs'],
    cons: ['Heaviest Nike racing shoe (~8.4 oz)', 'Overkill for shorter distances', 'Premium price'],
    link: 'https://www.nike.com/t/alphafly-3-mens-road-racing-shoes-5gZBgL',
    linkLabel: 'Shop Nike Alphafly 3',
  },
  {
    rank: 2,
    name: 'Nike Vaporfly 4',
    price: '~$260',
    weight: '~6.6 oz',
    best: 'Best all-around racing shoe',
    image: '/imgs/nike_vaporfly_4.avif',
    verdict:
      'The shoe that started the super shoe revolution. Lighter and more responsive than the Alphafly, the Vaporfly 4 delivers the full ZoomX + carbon plate benefit across all race distances from 5K to marathon. At ~6.6 oz it is the most versatile racing shoe Nike makes and the benchmark against which every other super shoe is measured.',
    pros: ['Lightest Nike option (~6.6 oz)', 'Versatile across all distances', 'Immediate, snappy energy return'],
    cons: ['Less cushioning than Alphafly on very long days', 'Runs slightly narrow', 'Premium price'],
    link: 'https://www.nike.com/t/vaporfly-4-mens-road-racing-shoes-HK05JWOf',
    linkLabel: 'Shop Nike Vaporfly 4',
  },
  {
    rank: 3,
    name: 'Adidas Adizero Adios Pro 4',
    price: '~$250',
    weight: '~8.0 oz',
    best: 'Best Nike alternative for marathon runners',
    image: '/imgs/adidas_adios_pro_4.avif',
    verdict:
      "Multiple World Marathon Major victories back up the Adios Pro's reputation. The unique Energy Rods 2.0 system and LightStrike Pro foam deliver a distinctive, propulsive feel that many runners prefer over ZoomX. Particularly strong at paces between 6:30 and 9:00 per mile.",
    pros: ['Unique Energy Rods provide excellent propulsion', 'Strong real-world race results', 'Slightly lower price than Alphafly'],
    cons: ['Different feel from Nike—takes adjustment', 'Slightly heavier than Vaporfly'],
    link: 'https://www.adidas.com/us/adizero-adios-pro-4-shoes/JR6369.html',
    linkLabel: 'Shop Adidas Adios Pro 4',
  },
  {
    rank: 4,
    name: 'Asics Metaspeed Sky Paris',
    price: '~$250',
    weight: '~7.3 oz',
    best: 'Best for stride-type runners',
    image: '/imgs/asics_metaspeed_sky_paris.jpg',
    verdict:
      "Engineered specifically for 'stride-type' runners who go faster by lengthening their stride rather than increasing cadence. The Sky Paris uses FF Turbo+ foam with a carbon plate and delivers a smooth, stable ride. The wider toe box and more stable platform make it excellent for taller runners and those who find the Vaporfly too narrow.",
    pros: ['Biomechanically tailored for stride-type runners', 'More stable platform than most super shoes', 'Wider toe box'],
    cons: ['Need to identify if you are stride vs cadence type', 'Less versatile than the Vaporfly'],
    link: 'https://www.asics.com/us/en-us/metaspeed-sky-paris/p/ANA_1013A123-750.html',
    linkLabel: 'Shop Asics Metaspeed Sky Paris',
  },
  {
    rank: 5,
    name: 'Saucony Endorphin Elite 2',
    price: '~$275',
    weight: '~7.6 oz',
    best: 'Best for wide feet and smooth transitions',
    image: '/imgs/saucony_endorphin_elite_2.jpg',
    verdict:
      "The Endorphin Elite 2 takes Saucony's PWRRUN HG foam—a higher-grade formulation than the standard PWRRUN PB—and pairs it with an S-shaped carbon fiber plate for a noticeably smooth heel-to-toe transition. The roomier toe box is a genuine advantage for runners who find Nike options too narrow. Race-proven at Boston and London.",
    pros: ['Roomier toe box suits wider feet', 'S-plate provides smooth transition', 'PWRRUN HG foam is highly responsive'],
    cons: ['Less proven at the elite front of the pack', 'Slightly softer feel than ZoomX'],
    link: 'https://www.saucony.com/en/endorphin-elite-2/59824U.html',
    linkLabel: 'Shop Saucony Endorphin Elite 2',
  },
  {
    rank: 6,
    name: 'New Balance FuelCell SC Elite v5',
    price: '~$250',
    weight: '~7.5 oz',
    best: 'Best for competitive amateur marathoners',
    image: '/imgs/new_balance_fuelcell_sc_elite_v5.webp',
    verdict:
      "New Balance has arrived as a serious super shoe competitor. The SC Elite v5 uses FuelCell foam with an energy arc carbon plate and delivers a smooth, efficient ride that genuinely challenges the top-tier options. The geometry is tuned specifically for marathon distances and the fit is among the most accommodating in the category.",
    pros: ['Accommodating fit across foot widths', 'Smooth, efficient marathon ride', 'Strong value vs. Nike'],
    cons: ['Less proven at world-class elite level', 'Newer in this specific super shoe tier'],
    link: 'https://www.newbalance.com/pd/fuelcell-supercomp-elite-v5/MRCELV5-50683-PMG-NA.html?dwvar_MRCELV5-50683-PMG-NA_style=MRCEL3UD',
    linkLabel: 'Shop NB FuelCell SC Elite v5',
  },
  {
    rank: 7,
    name: 'Hoka Rocket X 3',
    price: '~$250',
    weight: '~8.5 oz',
    best: 'Best for heel strikers',
    image: '/imgs/hoka_rocket_x_3.jpg',
    verdict:
      "Hoka's most aggressive carbon plate racer. The Rocket X 3 features a full-length carbon plate embedded in a thick PEBA foam stack with Hoka's distinctive rocker geometry. The pronounced meta-rocker makes this one of the best options for heel strikers who want carbon plate benefits—the rocker rolls you forward efficiently regardless of foot strike.",
    pros: ['Excellent for heel strikers', 'Distinctive rocker geometry aids efficiency', 'Well-cushioned for long races'],
    cons: ['Heavier than competitors (~8.5 oz)', 'Takes adaptation to rocker feel', 'Not as versatile for short races'],
    link: 'https://www.hokaoneone.com/mens-view-all/rocket-x/1113532.html',
    linkLabel: 'Shop Hoka Rocket X 3',
  },
  {
    rank: 8,
    name: 'Brooks Hyperion Elite 5',
    price: '~$275',
    weight: '~7.8 oz',
    best: 'Best for training in super shoes',
    image: '/imgs/brooks_hyperion_elite_5.jpg',
    verdict:
      "The Hyperion Elite 5 stands out for its durability. Most carbon plate shoes last 200–300 miles before the foam degrades; the Hyperion Elite 5 is rated for 400+ miles without significant performance drop-off. It uses Brooks's DNA Flash v2 nitrogen-infused foam with a full-length carbon plate. An excellent choice if you want to train in your race shoe.",
    pros: ['Outstanding durability (400+ miles)', 'More training-friendly than competitors', 'Well-regarded Brooks fit'],
    cons: ['Slightly less energy return than Nike at max effort', 'Not the lightest option'],
    link: 'https://www.brooksrunning.com/en_us/featured/unisex-running-shoes/hyperion-elite-5/100049.html',
    linkLabel: 'Shop Brooks Hyperion Elite 5',
  },
  {
    rank: 9,
    name: 'Puma Fast-R Nitro Elite 3',
    price: '~$300',
    weight: '~7.4 oz',
    best: 'Best for cadence-type runners seeking speed',
    image: '/imgs/puma_fast_r_nitro_elite_3.jpg',
    verdict:
      "Puma's most serious entry into the elite racing category. The Fast-R Nitro Elite 3 uses NITRO Elite foam—one of the most energy-returning foams outside of ZoomX—with a full-length carbon fiber plate. It sits high at $300 but delivers genuine elite-level performance. Backed by podium finishes at Abbott World Marathon Majors races.",
    pros: ['NITRO Elite foam is exceptionally responsive', 'Proven podium-level race results', 'Excellent energy return at fast paces'],
    cons: ['Most expensive option on this list', 'Limited availability vs. Nike/Adidas', 'Still building brand credibility in this category'],
    link: 'https://us.puma.com/us/en/pd/fast-r-nitro-elite-3-mens-road-running-shoes/312060',
    linkLabel: 'Shop Puma Fast-R Nitro Elite 3',
  },
  {
    rank: 10,
    name: 'On Cloudboom Strike',
    price: '~$280',
    weight: '~7.9 oz',
    best: 'Most innovative geometry on the market',
    image: '/imgs/on_cloudboom_strike.png',
    verdict:
      "On's most advanced racing shoe takes a different approach: instead of a traditional carbon plate, it uses a Speedboard—a full-length composite plate with integrated CloudTec Phase pods that compress and seal on impact. The result is a unique rolling sensation that feels different from every other shoe on this list. Worn by Hellen Obiri at the 2024 NYC Marathon.",
    pros: ['Genuinely different feel from carbon plate shoes', 'Elite racing pedigree', 'Innovative CloudTec Phase geometry'],
    cons: ['Very different from traditional super shoes—takes adaptation', 'Less widely available', '$280 price point'],
    link: 'https://www.on.com/en-us/products/cloudboom-strike-3me3048/',
    linkLabel: 'Shop On Cloudboom Strike',
  },
];

const theme = createTheme();

const BestMarathonShoes = () => (
  <ThemeProvider theme={theme}>
    <Helmet>
      <title>Best Marathon Racing Shoes 2025: Top 10 Carbon Plate Picks | SpeedBoostShoes.com</title>
      <meta
        name="description"
        content="The 10 best carbon plate marathon racing shoes of 2025. In-depth reviews of the Nike Alphafly 3, Vaporfly 4, Adidas Adios Pro 4, Asics Metaspeed Sky Paris, Saucony Endorphin Elite 2, NB FuelCell SC Elite v5, Hoka Rocket X 3, Brooks Hyperion Elite 5, Puma Fast-R Nitro Elite 3, and On Cloudboom Strike."
      />
      <link rel="canonical" href="https://www.speedboostshoes.com/best-marathon-racing-shoes-2025" />
      <meta property="og:title" content="Best Marathon Racing Shoes 2025 | SpeedBoostShoes.com" />
      <meta property="og:description" content="Reviews of the top 10 carbon plate marathon shoes: Nike Alphafly 3, Vaporfly 4, Adidas Adios Pro 4, Asics Metaspeed Sky Paris, and more." />
      <meta property="og:url" content="https://www.speedboostshoes.com/best-marathon-racing-shoes-2025" />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
    <CssBaseline />
    <NavBar />
    <Container maxWidth="md" sx={{ py: 6 }}>
      <article>
        <Typography component="h1" variant="h3" gutterBottom>
          Best Marathon Racing Shoes 2025: Top 10 Carbon Plate Picks
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Updated March 2025 · 12 min read
        </Typography>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          The super shoe era is fully established. Carbon fiber plates, ultra-responsive foam
          stacks, and precision geometry have become standard equipment at World Marathon Majors
          and local 5Ks alike. If you're racing in 2025 without a carbon plate shoe, you're
          giving up a measurable, research-backed performance advantage.
        </Typography>
        <Typography variant="body1" paragraph>
          But with ten serious options from major brands, which shoe is right for you?
          This guide breaks down the top ten carbon plate racing shoes of 2025—who they're
          best suited for, what to expect from each, and where to buy them.
        </Typography>

        <Typography component="h2" variant="h5" gutterBottom sx={{ mt: 3 }}>
          At a Glance: All 10 Shoes Compared
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 1, overflowX: 'auto' }}>
          <Table size="small" aria-label="Top 10 marathon racing shoes comparison">
            <TableHead>
              <TableRow>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Shoe</strong></TableCell>
                <TableCell align="center"><strong>Price</strong></TableCell>
                <TableCell align="center"><strong>Weight</strong></TableCell>
                <TableCell><strong>Best For</strong></TableCell>
                <TableCell align="center"><strong>Buy</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shoes.map((shoe) => (
                <TableRow key={shoe.rank}>
                  <TableCell>{shoe.rank}</TableCell>
                  <TableCell>{shoe.name}</TableCell>
                  <TableCell align="center">{shoe.price}</TableCell>
                  <TableCell align="center">{shoe.weight}</TableCell>
                  <TableCell>{shoe.best}</TableCell>
                  <TableCell align="center">
                    <Link href={shoe.link} target="_blank" rel="noopener noreferrer" variant="body2">
                      Shop
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' }, mb: 3 }}>
          Scroll right to see all columns.
        </Typography>

        <Typography component="h2" variant="h5" gutterBottom sx={{ mt: 3 }}>
          How We Evaluated These Shoes
        </Typography>
        <Typography variant="body1" paragraph>
          We assessed each shoe on four criteria: energy return and propulsion feel, fit and
          comfort over race distances, weight relative to cushioning, and overall value. All
          ten shoes have genuine carbon plate or composite plate technology—not a budget
          carbon-look plate—and real-world racing performance to back them up.
        </Typography>

        <Divider sx={{ my: 4 }} />

        {shoes.map((shoe) => (
          <Box key={shoe.name} sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip label={`#${shoe.rank}`} color="primary" size="small" />
              <Typography component="h2" variant="h5">
                {shoe.name} — {shoe.price}
              </Typography>
            </Box>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Best for: {shoe.best}
            </Typography>

            <Box
              component="img"
              src={shoe.image}
              alt={shoe.name}
              sx={{
                width: '100%',
                maxHeight: 320,
                objectFit: 'contain',
                bgcolor: 'grey.50',
                borderRadius: 1,
                my: 2,
                display: 'block',
              }}
            />

            <Typography variant="body1" paragraph>
              {shoe.verdict}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      Pros
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
                      {shoe.pros.map((pro) => (
                        <Typography component="li" variant="body2" key={pro}>{pro}</Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="error.main" gutterBottom>
                      Cons
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
                      {shoe.cons.map((con) => (
                        <Typography component="li" variant="body2" key={con}>{con}</Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Button
              href={shoe.link}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="small"
              sx={{ mt: 2 }}
            >
              {shoe.linkLabel}
            </Button>

            {shoe.rank < shoes.length && <Divider sx={{ mt: 5 }} />}
          </Box>
        ))}

        <Typography component="h2" variant="h5" gutterBottom sx={{ mt: 3 }}>
          Which Carbon Plate Shoe Is Right for You?
        </Typography>
        <Typography variant="body1" paragraph>
          If budget isn't a concern and you're marathoning: <strong>Nike Alphafly 3</strong> for
          maximum cushioning or <strong>Vaporfly 4</strong> if you prefer a lighter, more
          responsive feel.
        </Typography>
        <Typography variant="body1" paragraph>
          If you want a proven alternative to Nike: <strong>Adidas Adizero Adios Pro 4</strong>
          —backed by World Marathon Major wins and a distinct energy return feel.
        </Typography>
        <Typography variant="body1" paragraph>
          If you're a taller runner, prefer a more stable platform, or have wider feet:{' '}
          <strong>Asics Metaspeed Sky Paris</strong> or <strong>Saucony Endorphin Elite 2</strong>.
        </Typography>
        <Typography variant="body1" paragraph>
          If you're a heel striker: <strong>Hoka Rocket X 3</strong>—its rocker geometry works
          with your foot strike rather than against it.
        </Typography>
        <Typography variant="body1" paragraph>
          If you want to train in your race shoe: <strong>Brooks Hyperion Elite 5</strong>
          —rated for 400+ miles, far outlasting most competitors.
        </Typography>
        <Typography variant="body1" paragraph>
          If you want something genuinely different: <strong>On Cloudboom Strike</strong>
          —a completely different geometry that has already won at the NYC Marathon.
        </Typography>

        <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, mt: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Calculate How Much Time You Could Save
          </Typography>
          <Typography variant="body1" paragraph>
            Use our free running pace calculator to enter your current time and see what
            a 3% or 4% improvement would look like at your target race distance.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/">
            Open the Pace Calculator
          </Button>
        </Box>
      </article>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 4, mt: 4 }}>
        <Typography variant="h6" gutterBottom>Related Articles</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" component={RouterLink} to="/nike-alphafly-vs-vaporfly">
            Alphafly vs Vaporfly: Full Comparison
          </Button>
          <Button variant="outlined" component={RouterLink} to="/carbon-plate-running-shoes-explained">
            How Carbon Plate Shoes Work
          </Button>
          <Button variant="outlined" component={RouterLink} to="/how-much-faster-do-super-shoes-make-you">
            How Much Faster Do Super Shoes Make You?
          </Button>
        </Box>
      </Box>
    </Container>

    <Box sx={{ bgcolor: 'background.paper', p: 4 }} component="footer">
      <Typography variant="body2" color="text.secondary" align="center">
        © {new Date().getFullYear()} SpeedBoostShoes.com
      </Typography>
    </Box>
    <BackToCalculatorFab />
  </ThemeProvider>
);

export default BestMarathonShoes;
